import React, { useState, useEffect } from "react";
import { toast, Toaster } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, Clock, Lock, HandHeart, CalendarCheck, MapPin, ChatCircleDots, Leaf, Plus, Minus } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { specialities, timeSlots } from "../data/site";
import { useTreatments } from "../lib/treatments";
import Reveal from "../components/Reveal";
import useSeo from "../lib/useSeo";
import { useT, usePick } from "../lib/i18n";

export default function Consultations() {
  useSeo({
    title: "Book a Consultation | Yuva Ayurvedic Clinic Prayagraj",
    absoluteTitle: true,
    description: "45-minute private consultation with a trained Ayurvedic doctor in Prayagraj. Pick your date, time and concern — we'll confirm within 24 hours. Drug-free, unhurried men's wellness care.",
    keywords: "Book appointment Yuva Ayurvedic, Ayurvedic consultation Prayagraj, Men's health consultation India, Ayurvedic doctor Prayagraj booking",
  });
  const t = useT();
  const pick = usePick();
  const { items: dynamicTreatments } = useTreatments();
  const concernList = dynamicTreatments.length > 0 ? dynamicTreatments : specialities;

  // FAQPage JSON-LD — emitted only while this page is mounted so Google rewards
  // the first-visit FAQ with rich "People Also Ask"-style results.
  useEffect(() => {
    const faqs = [1, 2, 3, 4].map((n) => ({
      "@type": "Question",
      name: t(`faq.q${n}`),
      acceptedAnswer: { "@type": "Answer", text: t(`faq.a${n}`) },
    }));
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs,
    });
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById("faq-jsonld");
      if (s) s.remove();
    };
  }, [t]);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [concern, setConcern] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error(t("consult.err.date")); return; }
    if (!time) { toast.error(t("consult.err.time")); return; }
    if (!concern) { toast.error(t("consult.err.concern")); return; }
    if (!form.name || !form.email || !form.phone) { toast.error(t("consult.err.details")); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        preferred_date: date.toISOString().slice(0, 10),
        preferred_time: time,
        concern,
      });
      trackEvent("booking_submitted", {
        concern,
        preferred_date: date.toISOString().slice(0, 10),
        preferred_time: time,
      });
      setDone(true);
      toast.success(t("consult.success.toast"));
    } catch (err) {
      const msg = err?.response?.status === 429
        ? (err?.response?.data?.detail || t("consult.err.rate"))
        : (err?.response?.data?.detail || t("consult.err.generic"));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-24" data-testid="consultations-page">
      <Toaster richColors position="top-center"/>

      {/* What to expect on your visit — SHOWN FIRST so users understand the in-person, counselling-led model before scrolling to the form */}
      <section className="pt-24 lg:pt-32 pb-16" data-testid="what-to-expect-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline">{t("expect.overline")}</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-3xl leading-[0.95]">
              {t("expect.title.l1")} <span className="italic text-terracotta">{t("expect.title.l2")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {t("expect.sub")}
            </p>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {[
              { icon: CalendarCheck, k: "step1" },
              { icon: MapPin, k: "step2" },
              { icon: ChatCircleDots, k: "step3" },
              { icon: Leaf, k: "step4" },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.k} delay={i * 0.08}>
                  <div className="relative h-full bg-ivory border border-border/50 rounded-2xl p-7 group hover:border-terracotta/60 transition-colors" data-testid={`expect-${s.k}`}>
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-full bg-sage/15 text-sage flex items-center justify-center">
                        <Icon size={22} weight="duotone"/>
                      </div>
                      <span className="font-serif-display italic text-sage/40 text-4xl leading-none">0{i + 1}</span>
                    </div>
                    <h3 className="font-serif-display text-2xl mt-6 leading-tight">{t(`expect.${s.k}.h`)}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t(`expect.${s.k}.p`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <div className="overline">{t("consult.overline")}</div>
              <h2 className="font-serif-display text-5xl sm:text-6xl mt-6 leading-[0.95]">
                {t("consult.title.l1")}<br/>{t("consult.title.l2")}<br/><span className="italic text-terracotta">{t("consult.title.l3")}</span>
              </h2>
              <p className="mt-8 text-muted-foreground leading-relaxed">
                {t("consult.blurb")}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-10 space-y-5">
                <li className="flex gap-4"><Clock size={22} weight="duotone" className="text-sage shrink-0"/><div><div className="font-medium">{t("consult.bullet.visit.h")}</div><div className="text-sm text-muted-foreground">{t("consult.bullet.visit.p")}</div></div></li>
                <li className="flex gap-4"><Lock size={22} weight="duotone" className="text-sage shrink-0"/><div><div className="font-medium">{t("consult.bullet.drugfree.h")}</div><div className="text-sm text-muted-foreground">{t("consult.bullet.drugfree.p")}</div></div></li>
                <li className="flex gap-4"><HandHeart size={22} weight="duotone" className="text-sage shrink-0"/><div><div className="font-medium">{t("consult.bullet.unhurried.h")}</div><div className="text-sm text-muted-foreground">{t("consult.bullet.unhurried.p")}</div></div></li>
              </ul>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div className="bg-ivory border border-border/50 rounded-2xl p-10 text-center" data-testid="booking-success">
                <CheckCircle size={56} weight="duotone" className="text-sage mx-auto"/>
                <h2 className="font-serif-display text-4xl mt-6">{t("consult.success.title")} {form.name.split(" ")[0] || t("consult.success.friend")}.</h2>
                <p className="mt-4 text-muted-foreground max-w-md mx-auto">{t("consult.success.body.1")} <b>{date?.toDateString()}</b> {t("consult.success.body.2")} <b>{time}</b> {t("consult.success.body.3")}</p>
                <button onClick={() => { setDone(false); setDate(undefined); setTime(""); setConcern(""); setForm({ name:"",email:"",phone:"",message:"" }); }} className="mt-8 text-sm underline underline-offset-8 decoration-border" data-testid="booking-reset">{t("consult.success.another")}</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-ivory border border-border/50 rounded-2xl p-8 lg:p-10 space-y-7" data-testid="booking-form">
                <div>
                  <div className="overline mb-3">{t("consult.step1")}</div>
                  <div className="bg-cream rounded-xl p-3 border border-border/60 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      className="rounded-md"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                <div>
                  <div className="overline mb-3">{t("consult.step2")}</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                    {timeSlots.map(tslot => (
                      <button
                        type="button"
                        key={tslot}
                        onClick={() => setTime(tslot)}
                        data-testid={`time-${tslot.replace(/[: ]/g,'')}`}
                        className={`rounded-full border px-4 py-2 text-sm transition-all ${
                          time === tslot ? "bg-forest text-cream border-forest" : "border-border hover:border-forest"
                        }`}
                      >{tslot}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="overline mb-3">{t("consult.step3")}</div>
                  <Select value={concern} onValueChange={setConcern}>
                    <SelectTrigger data-testid="concern-select" className="bg-cream h-12 rounded-xl"><SelectValue placeholder={t("consult.placeholder.concern")}/></SelectTrigger>
                    <SelectContent>
                      {concernList.map(s => <SelectItem key={s.slug} value={s.title}>{pick(s, "title")}</SelectItem>)}
                      <SelectItem value="General wellness consultation">{t("consult.option.general")}</SelectItem>
                      <SelectItem value="Ayurvedic detox programme">{t("consult.option.detox")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="overline mb-3">{t("consult.step4")}</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>{t("consult.field.name")}</Label><Input data-testid="field-name" value={form.name} onChange={set("name")} className="bg-cream h-12 mt-2" required/></div>
                    <div><Label>{t("consult.field.phone")}</Label><Input data-testid="field-phone" value={form.phone} onChange={set("phone")} className="bg-cream h-12 mt-2" required/></div>
                    <div className="sm:col-span-2"><Label>{t("consult.field.email")}</Label><Input data-testid="field-email" type="email" value={form.email} onChange={set("email")} className="bg-cream h-12 mt-2" required/></div>
                    <div className="sm:col-span-2"><Label>{t("consult.field.message")}</Label><Textarea data-testid="field-message" value={form.message} onChange={set("message")} className="bg-cream mt-2 min-h-[100px]" placeholder={t("consult.field.message.placeholder")}/></div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="w-full rounded-full bg-forest text-cream py-4 font-medium tracking-wide hover:bg-terracotta transition-all duration-500 disabled:opacity-60"
                >
                  {submitting ? t("cta.sending") : t("cta.request")}
                </button>
                <p className="text-xs text-muted-foreground text-center">{t("consult.disclaimer")}</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* First-visit FAQ — sits right after the booking form to answer common hesitations */}
      <section className="py-20 lg:py-28 bg-ivory/50" data-testid="first-visit-faq-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline">{t("faq.overline")}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
              {t("faq.title.l1")}<br/><span className="italic text-terracotta">{t("faq.title.l2")}</span>
            </h2>
            <p className="mt-8 text-muted-foreground max-w-sm">
              A few quick answers so you walk into our Prayagraj clinic feeling informed and at ease.
            </p>
          </Reveal>
          <div className="md:col-span-7 space-y-4">
            {[1, 2, 3, 4].map((n, i) => (
              <Reveal key={n} delay={i * 0.06}>
                <FaqItem
                  q={t(`faq.q${n}`)}
                  a={t(`faq.a${n}`)}
                  testId={`faq-q${n}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-cream border border-border/50 rounded-2xl overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full text-left flex items-start justify-between gap-6 p-6 md:p-7 hover:bg-ivory/70 transition-colors"
      >
        <span className="font-serif-display text-xl md:text-2xl leading-snug pr-4">{q}</span>
        <span className={`mt-1 w-8 h-8 rounded-full bg-forest text-cream flex items-center justify-center shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
          {open ? <Minus size={14} weight="bold"/> : <Plus size={14} weight="bold"/>}
        </span>
      </button>
      <div
        className={`grid transition-all duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <p className="px-6 md:px-7 pb-7 text-muted-foreground leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}
